import { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api, { asegurarToken } from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { formatApiError } from '@/lib/utils';
import { Send, MessageSquare, Loader2, Hash, Circle, Paperclip, FileText, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'lexorbit_token';
// Token fresco desde localStorage (el apiClient lo renueva). Evita usar uno viejo.
const leerToken = () => localStorage.getItem(TOKEN_KEY);
const leerUserId = () => {
  try { const t = leerToken(); return t ? JSON.parse(atob(t.split('.')[1])).sub : null; }
  catch { return null; }
};
// URL absoluta de un archivo servido por el backend (archivo_url viene como /api/static/...).
const fileUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${API}${u}`));
const esImagen = (m) => {
  if (m.archivo_tipo && m.archivo_tipo.startsWith('image/')) return true;
  const n = (m.archivo_nombre || '').toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp)$/.test(n);
};

export default function Chat() {
  const [salas, setSalas] = useState([]);
  const [salaActiva, setSalaActiva] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const wsRef = useRef(null);
  const finRef = useRef(null);
  const fileRef = useRef(null);
  const userId = leerUserId();

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes]);

  const cargarSalas = useCallback(async () => {
    try {
      const r = await api.get('/api/chat/salas');
      setSalas(r.data.data || []);
      if (!salaActiva && r.data.data?.length) setSalaActiva(r.data.data[0]);
    } finally { setLoading(false); }
  }, [salaActiva]);

  useEffect(() => { cargarSalas(); }, []); // eslint-disable-line

  // WebSocket — apunta al BACKEND (REACT_APP_BACKEND_URL), con token fresco de
  // localStorage. Si lo rechazan por token vencido (4001) renueva y reconecta;
  // ante caídas de red reintenta solo.
  useEffect(() => {
    if (!userId) return;
    let cerrado = false;
    let reconnectTimer = null;
    let refrescado = false;

    const wsBase = API
      ? API.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

    const conectar = () => {
      const token = leerToken();
      if (!token || cerrado) return;
      const ws = new WebSocket(`${wsBase}/api/chat/ws/${userId}?token=${token}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        const data = JSON.parse(ev.data);
        if (data.tipo === 'mensaje') {
          setMensajes((m) => (data.sala_id === salaActiva?.id ? [...m, data] : m));
          cargarSalas();
        }
      };
      ws.onclose = async (ev) => {
        if (cerrado) return;
        // 4001 = token inválido/expirado → renovar una vez y reconectar.
        if (ev.code === 4001 && !refrescado) {
          refrescado = true;
          try { await asegurarToken(); } catch { /* sin refresh válido */ }
          conectar();
          return;
        }
        // 4003 (inactivo) / 4500 (DB) / 4001 ya reintentado: no insistir.
        if ([4001, 4003, 4500].includes(ev.code)) return;
        // Caída de red u otro: reintento con backoff corto.
        reconnectTimer = setTimeout(conectar, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    conectar();
    return () => { cerrado = true; clearTimeout(reconnectTimer); try { wsRef.current?.close(); } catch { /* noop */ } };
  }, [userId, salaActiva?.id]); // eslint-disable-line

  // Cargar mensajes al cambiar de sala
  useEffect(() => {
    if (!salaActiva) return;
    (async () => {
      const r = await api.get(`/api/chat/salas/${salaActiva.id}/mensajes`);
      setMensajes(r.data.data || []);
      api.post(`/api/chat/salas/${salaActiva.id}/leido`).catch(() => {});
    })();
  }, [salaActiva]);

  const wsListo = () => wsRef.current?.readyState === WebSocket.OPEN;

  const enviar = (e) => {
    e.preventDefault();
    const texto = input.trim();
    if (!texto || !salaActiva) return;
    if (!wsListo()) { showAlert('Reconectando al chat… probá de nuevo en un segundo.', 'warning'); return; }
    wsRef.current.send(JSON.stringify({ tipo: 'mensaje', sala_id: salaActiva.id, contenido: texto }));
    setInput('');
  };

  const onElegirArchivo = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = ''; // permitir re-subir el mismo archivo
    if (!archivo || !salaActiva) return;
    if (!wsListo()) { showAlert('Reconectando al chat… probá de nuevo en un segundo.', 'warning'); return; }
    if (archivo.size > 50 * 1024 * 1024) { showAlert('El archivo supera el máximo de 50 MB.', 'warning'); return; }

    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      const r = await api.post('/api/chat/archivos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { url, nombre, tipo } = r.data;
      wsRef.current.send(JSON.stringify({
        tipo: 'mensaje', sala_id: salaActiva.id, contenido: input.trim() || '',
        archivo_url: url, archivo_nombre: nombre, archivo_tipo: tipo,
      }));
      setInput('');
    } catch (err) {
      showAlert(formatApiError(err.response?.data?.detail, 'No se pudo subir el archivo.'));
    } finally { setSubiendo(false); }
  };

  const nombreSala = (s) => s.tipo === 'privada'
    ? (s.otro_usuario ? `${s.otro_usuario.nombre} ${s.otro_usuario.apellido || ''}`.trim() : 'Privado')
    : s.nombre;

  return (
    <DashboardLayout title="Chat interno">
      <div className="flex h-[calc(100vh-9rem)] bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
        {/* Salas */}
        <div className="w-64 border-r border-[rgba(201,164,76,0.12)] overflow-y-auto flex-shrink-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#C9A44C]" /></div>
          ) : salas.map((s) => (
            <button key={s.id} onClick={() => setSalaActiva(s)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 transition-all ${salaActiva?.id === s.id ? 'bg-[#C9A44C]/10' : 'hover:bg-white/[0.03]'}`}>
              <div className="flex items-center gap-2">
                {s.tipo === 'privada'
                  ? <Circle className={`w-2 h-2 ${s.otro_online ? 'fill-green-400 text-green-400' : 'fill-[#555] text-[#555]'}`} />
                  : <Hash className="w-3.5 h-3.5 text-[#C9A44C]" />}
                <span className="text-sm truncate">{nombreSala(s)}</span>
                {s.no_leidos > 0 && <span className="ml-auto text-[10px] bg-[#C9A44C] text-[#070707] rounded-full px-1.5">{s.no_leidos}</span>}
              </div>
              {s.ultimo_mensaje && <div className="text-[11px] text-[#777] truncate mt-0.5">{s.ultimo_mensaje}</div>}
            </button>
          ))}
        </div>

        {/* Mensajes */}
        <div className="flex-1 flex flex-col">
          {!salaActiva ? (
            <div className="flex-1 flex items-center justify-center text-[#666]">
              <div className="text-center"><MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="text-sm">Seleccioná una conversación</p></div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-[rgba(201,164,76,0.12)] text-sm">{nombreSala(salaActiva)}</div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {mensajes.map((m, i) => {
                  const propio = m.remitente_id === userId;
                  return (
                    <div key={m.id || i} className={`flex ${propio ? 'justify-end' : ''}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 text-sm ${propio ? 'bg-[#C9A44C] text-[#070707]' : 'bg-white/[0.04] border border-white/10'}`}>
                        {!propio && <div className="text-[10px] text-[#C9A44C] mb-0.5">{m.nombre} {m.apellido}</div>}
                        {m.archivo_url && (
                          esImagen(m) ? (
                            <a href={fileUrl(m.archivo_url)} target="_blank" rel="noreferrer" className="block mb-1">
                              <img src={fileUrl(m.archivo_url)} alt={m.archivo_nombre || 'imagen'}
                                className="rounded-lg max-h-64 max-w-full object-contain border border-black/10" />
                            </a>
                          ) : (
                            <a href={fileUrl(m.archivo_url)} target="_blank" rel="noreferrer" download
                              className={`flex items-center gap-2 mb-1 rounded-lg px-2.5 py-2 ${propio ? 'bg-black/10 hover:bg-black/20' : 'bg-white/[0.04] hover:bg-white/[0.08] border border-white/10'} transition-colors`}>
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate max-w-[180px] text-[12px]">{m.archivo_nombre || 'archivo'}</span>
                              <Download className="w-3.5 h-3.5 ml-auto flex-shrink-0 opacity-70" />
                            </a>
                          )
                        )}
                        {m.contenido && <div className="whitespace-pre-wrap">{m.contenido}</div>}
                      </div>
                    </div>
                  );
                })}
                <div ref={finRef} />
              </div>
              <form onSubmit={enviar} className="p-4 border-t border-[rgba(201,164,76,0.12)] flex gap-2 items-center">
                <input ref={fileRef} type="file" className="hidden" onChange={onElegirArchivo}
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={subiendo}
                  title="Adjuntar archivo (PDF, imagen, Word, Excel…)"
                  className="text-[#C9A44C] hover:text-[#E0C878] transition-colors flex-shrink-0 disabled:opacity-40">
                  {subiendo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                </button>
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribí un mensaje..."
                  className="flex-1 bg-white/[0.04] border border-[rgba(201,164,76,0.15)] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C]" />
                <button type="submit" className="text-[#070707] px-4 py-2.5 rounded-lg flex items-center disabled:opacity-40" disabled={!input.trim()}
                  style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}><Send className="w-4 h-4" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
